import { useState, useCallback, useMemo } from 'react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useApiMutation } from '@/hooks/useApiMutation'
import { useToast } from '@/components/ToastContainer'
import { apiFetch } from '@/lib/api'
import { Badge, Alert, AdminTable, Input, Select, Button } from '@/ui'
import type { TableColumn } from '@/ui'
import { DiscoverModelModal } from '@/components/DiscoverModelModal'
import { EditModelModal } from '@/components/EditModelModal'
import { CreateVariantModal } from '@/components/CreateVariantModal'
import styles from './AIModels.module.css'

// ── Image Model ──

interface ImageModelSetting {
  id: string
  name: string
  provider: string
  providerType: string
  costPerImage: number
  description: string
  facePreservation: string
  speed: string
  providerModel: string
  supportsReferenceImage: boolean
  aspectRatio: string | null
  imageSize: string | null
  modelType: 'image'
  enabled: boolean
  isRecommended: boolean
  isDefault: boolean
  sortOrder: number
  updatedAt: string | null
}

// ── Text Model ──

interface TextModelSetting {
  id: string
  name: string
  provider: string
  providerType: string
  costPer1kInputTokens: number
  costPer1kOutputTokens: number
  maxContextTokens: number
  providerModel: string
  description: string
  modelType: 'text'
  enabled: boolean
  isDefault: boolean
  sortOrder: number
  updatedAt: string | null
}

export type UnifiedModel = ImageModelSetting | TextModelSetting

interface AIModelsResponse {
  imageModels: ImageModelSetting[]
  textModels: TextModelSetting[]
}

const SPEED_COLORS: Record<string, 'green' | 'yellow' | 'red'> = {
  fast: 'green',
  medium: 'yellow',
  slow: 'red',
}

const FACE_COLORS: Record<string, 'green' | 'blue' | 'yellow' | 'red'> = {
  excellent: 'green',
  good: 'blue',
  fair: 'yellow',
  poor: 'red',
}

const TYPE_FILTER_OPTIONS = [
  { value: '', label: '전체 타입' },
  { value: 'image', label: '이미지' },
  { value: 'text', label: '텍스트' },
]

export function AIModels() {
  const { addToast } = useToast()
  const [searchQuery, setSearchQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [discoverOpen, setDiscoverOpen] = useState(false)
  const [editingModel, setEditingModel] = useState<UnifiedModel | null>(null)
  const [variantSource, setVariantSource] = useState<UnifiedModel | null>(null)

  const { data, isLoading, error, refetch } = useApiFetch<AIModelsResponse>(
    '/api/admin/ai-models',
  )

  const { trigger } = useApiMutation<AIModelsResponse, Record<string, unknown>>(
    '/api/admin/ai-models',
    'PATCH',
  )

  // ── 통합 모델 목록 (이미지 → 텍스트 순, sortOrder 정렬) ──

  const allModels = useMemo<UnifiedModel[]>(() => {
    if (!data) return []
    return [...data.imageModels, ...data.textModels].sort((a, b) => {
      if (a.modelType !== b.modelType) return a.modelType === 'image' ? -1 : 1
      return a.sortOrder - b.sortOrder
    })
  }, [data])

  // ── providerModel별 변형 수 계산 ──

  const variantCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const m of allModels) {
      const pm = m.providerModel
      counts.set(pm, (counts.get(pm) ?? 0) + 1)
    }
    return counts
  }, [allModels])

  // ── 클라이언트 사이드 검색/필터 ──

  const filteredModels = useMemo(() => {
    let list = allModels
    if (typeFilter) list = list.filter((m) => m.modelType === typeFilter)
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      list = list.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.provider.toLowerCase().includes(q) ||
          m.id.toLowerCase().includes(q),
      )
    }
    return list
  }, [allModels, typeFilter, searchQuery])

  // ── 통합 토글 핸들러 ──

  const handleToggleEnabled = useCallback(
    async (row: UnifiedModel, newValue: boolean) => {
      const result = await trigger({ modelId: row.id, modelType: row.modelType, enabled: newValue })
      if (!result) throw new Error('설정 변경에 실패했습니다.')
      addToast({ type: 'success', message: '설정이 변경되었습니다.' })
      refetch()
    },
    [trigger, addToast, refetch],
  )

  const handleToggleRecommended = useCallback(
    async (row: UnifiedModel, newValue: boolean) => {
      const result = await trigger({ modelId: row.id, modelType: row.modelType, isRecommended: newValue })
      if (!result) throw new Error('설정 변경에 실패했습니다.')
      addToast({ type: 'success', message: '설정이 변경되었습니다.' })
      refetch()
    },
    [trigger, addToast, refetch],
  )

  const handleToggleDefault = useCallback(
    async (row: UnifiedModel, newValue: boolean) => {
      const result = await trigger({ modelId: row.id, modelType: row.modelType, isDefault: newValue })
      if (!result) throw new Error('설정 변경에 실패했습니다.')
      addToast({ type: 'success', message: '설정이 변경되었습니다.' })
      refetch()
    },
    [trigger, addToast, refetch],
  )

  // ── 삭제 핸들러 ──

  const handleDelete = useCallback(
    async (row: UnifiedModel) => {
      await apiFetch(`/api/admin/ai-models/${row.id}`, { method: 'DELETE' })
      refetch()
    },
    [refetch],
  )

  // ── 통합 컬럼 ──

  const columns: TableColumn<UnifiedModel>[] = useMemo(
    () => [
      {
        key: 'sortOrder',
        label: '#',
        render: (row) => String(row.sortOrder),
        width: '40px',
      },
      {
        key: 'modelType',
        label: '타입',
        width: '80px',
        render: (row) => (
          <Badge color={row.modelType === 'image' ? 'blue' : 'green'}>
            {row.modelType === 'image' ? '이미지' : '텍스트'}
          </Badge>
        ),
      },
      {
        key: 'name',
        label: '모델',
        render: (row) => (
          <div>
            <div className={styles.modelName}>
              {row.name}
              {(variantCounts.get(row.providerModel) ?? 0) > 1 && (
                <span className={styles.variantBadge}>
                  변형 {allModels.filter(m => m.providerModel === row.providerModel).indexOf(row) + 1}/{variantCounts.get(row.providerModel)}
                </span>
              )}
            </div>
            <div className={styles.modelId}>{row.id}</div>
            {row.modelType === 'image' && (
              <div className={styles.modelMeta}>
                <Badge color={SPEED_COLORS[(row as ImageModelSetting).speed] ?? 'gray'}>
                  {(row as ImageModelSetting).speed}
                </Badge>
                <Badge color={FACE_COLORS[(row as ImageModelSetting).facePreservation] ?? 'gray'}>
                  {(row as ImageModelSetting).facePreservation}
                </Badge>
                {(row as ImageModelSetting).supportsReferenceImage && (
                  <Badge color="green">ref</Badge>
                )}
                {(row as ImageModelSetting).aspectRatio && (
                  <Badge color="gray">{(row as ImageModelSetting).aspectRatio}</Badge>
                )}
                {(row as ImageModelSetting).imageSize && (
                  <Badge color="gray">{(row as ImageModelSetting).imageSize}</Badge>
                )}
              </div>
            )}
            {row.modelType === 'text' && (
              <div className={styles.modelMeta}>
                <Badge color="gray">
                  {((row as TextModelSetting).maxContextTokens / 1000).toFixed(0)}K ctx
                </Badge>
              </div>
            )}
          </div>
        ),
      },
      { key: 'provider', label: '프로바이더', width: '90px' },
      {
        key: 'cost',
        label: '비용',
        width: '140px',
        align: 'right' as const,
        render: (row) => {
          if (row.modelType === 'image') {
            return `$${(row as ImageModelSetting).costPerImage.toFixed(2)}/img`
          }
          const t = row as TextModelSetting
          return (
            <div className={styles.costDetail}>
              <div>In: ${t.costPer1kInputTokens.toFixed(4)}/1k</div>
              <div>Out: ${t.costPer1kOutputTokens.toFixed(4)}/1k</div>
            </div>
          )
        },
      },
      { key: 'description', label: '설명' },
    ],
    [],
  )

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <Input
          size="sm"
          placeholder="모델명, 프로바이더, ID 검색..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Select
          size="sm"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          options={TYPE_FILTER_OPTIONS}
        />
        <Button size="sm" variant="primary" onClick={() => setDiscoverOpen(true)}>
          모델 추가
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <AdminTable<UnifiedModel>
        columns={columns}
        data={filteredModels}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        emptyMessage={searchQuery || typeFilter ? '검색 결과가 없습니다' : '등록된 모델이 없습니다'}
        toggles={[
          { label: '활성', getValue: (row) => row.enabled, onToggle: handleToggleEnabled },
          {
            label: '추천',
            getValue: (row) =>
              row.modelType === 'image' ? (row as ImageModelSetting).isRecommended : false,
            onToggle: handleToggleRecommended,
            disabled: (row) => row.modelType !== 'image',
          },
          { label: '기본', getValue: (row) => row.isDefault, onToggle: handleToggleDefault },
        ]}
        actions={[
          {
            label: '수정',
            variant: 'ghost',
            onClick: (row) => setEditingModel(row),
          },
          {
            label: '변형 추가',
            variant: 'ghost',
            onClick: (row) => setVariantSource(row),
          },
        ]}
        delete={{
          message: (row) => `"${row.name}" 모델을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`,
          onDelete: handleDelete,
        }}
      />

      <DiscoverModelModal
        open={discoverOpen}
        onClose={() => setDiscoverOpen(false)}
        onRegistered={() => {
          refetch()
          setDiscoverOpen(false)
        }}
      />

      <EditModelModal
        open={editingModel !== null}
        model={editingModel}
        onClose={() => setEditingModel(null)}
        onSaved={() => {
          refetch()
          setEditingModel(null)
        }}
      />

      <CreateVariantModal
        open={variantSource !== null}
        sourceModel={variantSource}
        onClose={() => setVariantSource(null)}
        onCreated={() => {
          refetch()
          setVariantSource(null)
        }}
      />
    </div>
  )
}
